import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { MapPin, Globe, Linkedin, Edit, Eye, EyeOff, UserPlus, PlayCircle, Clock, DollarSign, Briefcase, Code2, Check, ArrowRight, Video, Star, AlertTriangle, XCircle, MessageCircle, Loader2, Brain, ExternalLink } from 'lucide-react';
import { useFreelancerProfile } from '@/lib/hooks/use-freelancer-profile';
import { PortfolioSection } from '@/components/portfolio/portfolio-section';
import { WorkExperienceSection } from '@/components/profile/work-experience-section';
import { SignUpButton, useUser } from '@clerk/clerk-react';
import { GlobalNavigation } from '@/components/global-navigation';
import { AnimatedLoader } from '@/components/ui/animated-loader';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';
import { getVideoEmbedInfo } from '@/lib/utils/video';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { adminService } from '@/lib/api/services/admin.service';
import { toast } from 'sonner';
import { chatService } from '@/lib/api/services/chat.service';
import { freelancerService } from '@/lib/api/services/freelancer.service';
import { useProfile } from '@/lib/hooks/use-profile';
import { useOwnClientProfile } from '@/lib/hooks/use-own-client-profile';
import { useClientProfileCompletion } from '@/lib/hooks/use-client-profile-completion';
import { FadeIn } from '@/components/motion';
import { StartChatConfirmationDialog } from '@/components/chat/start-chat-confirmation-dialog';

export default function FreelancerProfile() {
  const { id } = useParams<{ id: string }>();
  const { freelancerProfile: profile, isLoading, error, isOwner, isAdmin, isEnabled, isFeatured, reviewStatus } = useFreelancerProfile(id);
  const { isSignedIn } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { profile: userProfile } = useProfile();
  const [isStartingChat, setIsStartingChat] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [viewTracked, setViewTracked] = useState(false);
  const viewStartTimeRef = useRef<number | null>(null);

  // Check if user is a client (only clients can start chats from freelancer profiles)
  const isClient = userProfile?.is_client ?? false;
  
  // Get client profile completion status for clients
  const { clientProfile } = useOwnClientProfile();
  const profileCompletion = useClientProfileCompletion(clientProfile);

  // Scroll to top when landing on the page or when id changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  // Track profile view (only for authenticated, non-owner users who stay ≥5 seconds)
  useEffect(() => {
    // Only track if authenticated and not owner
    if (!isSignedIn || isOwner || !id) return;
    
    // Reset view tracking state when profile ID changes
    setViewTracked(false);
    viewStartTimeRef.current = null;
    
    // Record start time
    viewStartTimeRef.current = Date.now();
    
    // Set timeout for 5 seconds
    const timer = setTimeout(async () => {
      if (viewStartTimeRef.current) {
        const timeOnPage = Date.now() - viewStartTimeRef.current;
        if (timeOnPage >= 5000 && !viewTracked && id) {
          try {
            await freelancerService.trackProfileView(id);
            setViewTracked(true);
          } catch (error) {
            // Silently fail - view tracking should not break user experience
            console.debug('Failed to track profile view:', error);
          }
        }
      }
    }, 5000);
    
    return () => {
      clearTimeout(timer);
    };
  }, [id, isSignedIn, isOwner, viewTracked]);

  // Admin mutations for profile management
  const toggleEnabledMutation = useMutation({
    mutationFn: async ({ enable }: { enable: boolean }) => {
      if (!id) throw new Error("Missing profile ID");
      if (enable) {
        await adminService.enableFreelancerProfile(id);
      } else {
        await adminService.disableFreelancerProfile(id);
      }
      return { enable };
    },
    onSuccess: ({ enable }) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      toast.success(`Genius ${enable ? 'enabled' : 'disabled'} successfully`);
    },
    onError: () => toast.error('Failed to update status'),
  });

  const toggleFeaturedMutation = useMutation({
    mutationFn: async ({ feature }: { feature: boolean }) => {
      if (!id) throw new Error("Missing profile ID");
      if (feature) {
        await adminService.featureFreelancerProfile(id);
      } else {
        await adminService.unfeatureFreelancerProfile(id);
      }
      return { feature };
    },
    onSuccess: ({ feature }) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      queryClient.invalidateQueries({ queryKey: ["featured-freelancers"] });
      toast.success(`Genius ${feature ? 'featured' : 'unfeatured'} successfully`);
    },
    onError: () => toast.error('Failed to update featured status'),
  });

  const reviewMutation = useMutation({
    mutationFn: async ({ action }: { action: 'approve' | 'reject' | 'request_changes' }) => {
      if (!id) throw new Error("Missing profile ID");
      
      if (action === 'approve') {
        await adminService.approveFreelancerProfile(id);
      } else if (action === 'reject') {
        await adminService.rejectFreelancerProfile(id);
      } else {
        await adminService.requestChangesForFreelancerProfile(id, 'Changes requested');
      }

      return { action };
    },
    onSuccess: ({ action }) => {
      queryClient.invalidateQueries({ queryKey: ["freelancer-profile", id] });
      queryClient.invalidateQueries({ queryKey: ["admin-freelancer-profiles"] });
      const actionText = action === 'approve' ? 'approved' : action === 'reject' ? 'rejected' : 'requested changes for';
      toast.success(`Profile ${actionText} successfully`);
    },
    onError: () => toast.error('Failed to perform review action'),
  });

  // Surface a single loading state for any admin action
  const isActionPending = toggleEnabledMutation.isPending || toggleFeaturedMutation.isPending || reviewMutation.isPending;

  const handleStartChat = async () => {
    if (!id) return

    // Only clients can start chats
    if (!isClient) {
      toast.error('Only clients can start conversations with freelancers')
      return
    }

    // Check if client profile is complete
    if (!profileCompletion.isComplete) {
      toast.error('Please complete your profile before starting a conversation')
      navigate('/client/profile')
      return
    }

    // Show confirmation dialog
    setShowConfirmDialog(true)
  }

  const confirmStartChat = async () => {
    if (!id) return

    setShowConfirmDialog(false)
    setIsStartingChat(true)
    try {
      // Get the user_id from the freelancer profile
      // The id is the profile ID, we need to fetch the profile to get user_id
      let freelancerUserId: string
      
      // Check if user_id is already available in the profile object
      if (profile?.user_id && typeof profile.user_id === 'string') {
        freelancerUserId = profile.user_id
      } else {
        // Fetch the profile to get the user_id
        const profileData = await freelancerService.getProfile(id)
        if (!profileData.user_id) {
          throw new Error('Freelancer user ID not found')
        }
        freelancerUserId = profileData.user_id
      }

      // Create or find existing conversation using user_id (not profile ID)
      const conversation = await chatService.createOrFindConversation(freelancerUserId)
      
      // Navigate to chat page with conversation ID
      navigate(`/client/chat?conversationId=${conversation.id}`)
    } catch (error) {
      console.error('Failed to start chat:', error)
      const errorMessage = error instanceof Error ? error.message : 'Failed to start chat. Please try again.'
      toast.error(errorMessage)
    } finally {
      setIsStartingChat(false)
    }
  }

  if (isLoading) {
    return (
      <>
        <GlobalNavigation />
        <div className="min-h-screen bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <AnimatedLoader message="Loading profile..." />
            </div>
          </div>
        </div>
      </>
    );
  }

  if (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    return (
      <>
        <GlobalNavigation />
        <div className="min-h-screen bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col items-center justify-center h-64 space-y-4">
              <div className="text-lg text-red-600">
                {errorMessage === 'Access denied' ? 'This profile is private' :
                 errorMessage === 'Profile not found' ? 'Profile not found' :
                 `Error: ${errorMessage}`}
              </div>
              <Link to="/">
                <Button variant="outline" className="rounded-full">Go Home</Button>
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (!profile) {
    return (
      <>
        <GlobalNavigation />
        <div className="min-h-screen bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex items-center justify-center h-64">
              <div className="text-lg text-slate-600">Profile not found</div>
            </div>
          </div>
        </div>
      </>
    );
  }

  const initials = profile.display_name
    ?.split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || '?';

  const skills = Array.isArray(profile.skills) ? profile.skills as string[] : [];
  const languages = Array.isArray(profile.languages) ? profile.languages as string[] : [];

  // Type-safe access to new fields
  const profileWithNewFields = profile as typeof profile & {
    video_intro_urls?: string[] | null;
    availability_status?: string | null;
    rate_range_min_cents?: number | null;
    rate_range_max_cents?: number | null;
    view_count?: number;
  };

  const videoIntroUrls = Array.isArray(profileWithNewFields.video_intro_urls) ? profileWithNewFields.video_intro_urls : [];
  const availabilityStatus = profileWithNewFields.availability_status || null;
  const rateRangeMin = profileWithNewFields.rate_range_min_cents ? (profileWithNewFields.rate_range_min_cents / 100).toFixed(0) : null;
  const rateRangeMax = profileWithNewFields.rate_range_max_cents ? (profileWithNewFields.rate_range_max_cents / 100).toFixed(0) : null;

  const getAvailabilityBadge = (status: string | null) => {
    switch (status) {
      case 'available':
        return { label: 'Available', className: 'bg-emerald-100 text-emerald-800' };
      case 'busy':
        return { label: 'Busy', className: 'bg-orange-100 text-orange-800' };
      case 'part-time':
        return { label: 'Part-time', className: 'bg-blue-100 text-blue-800' };
      case 'unavailable':
        return { label: 'Unavailable', className: 'bg-slate-100 text-slate-800' };
      default:
        return null;
    }
  };

  const availabilityBadge = getAvailabilityBadge(availabilityStatus);

  const getRateDisplay = () => {
    if (rateRangeMin && rateRangeMax) return `$${rateRangeMin}-${rateRangeMax}/hr`;
    if (rateRangeMin && !rateRangeMax) return `$${rateRangeMin}+/hr`;
    if (!rateRangeMin && rateRangeMax) return `Up to $${rateRangeMax}/hr`;
    if (profile.hourly_rate_cents) return `$${Math.round(profile.hourly_rate_cents / 100)}/hr`;
    return null;
  };

  const rateDisplay = getRateDisplay();

  return (
    <>
      <GlobalNavigation />
      <div className="min-h-screen bg-slate-50 pb-8">
        {/* Header Section */}
        <div
          className="pt-8 sm:pt-10 lg:pt-12 pb-0 px-4 sm:px-6 lg:px-8"
          style={{
            background:
              'linear-gradient(180deg, rgba(232, 240, 254, 1) 0%, rgba(240, 245, 255, 1) 50%, rgba(248, 250, 252, 1) 100%)',
          }}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <FadeIn>
              <div
                className={cn(
                  'relative overflow-hidden rounded-[32px] px-6 py-6 sm:px-8 sm:py-7 lg:px-10 lg:py-8',
                  'bg-gradient-to-r from-[#4669ed] to-[#3654e9] text-white shadow-[0px_18px_45px_rgba(70,105,237,0.35)]'
                )}
              >
                {/* Background decoration */}
                <div className="pointer-events-none absolute inset-0 opacity-15">
                  <div className="absolute -top-16 -right-10 h-48 w-48 rounded-full bg-white/20 blur-2xl" />
                  <div className="absolute -bottom-20 -left-6 h-56 w-56 rounded-full bg-white/10 blur-3xl" />
                </div>

                <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-white/80">
                      <Brain className="h-5 w-5 sm:h-6 sm:w-6" />
                      <span className="text-xs font-semibold uppercase tracking-[0.14em] sm:text-sm">
                        Genius Profile
                      </span>
                    </div>
                    <h1 className="text-2xl font-semibold sm:text-3xl lg:text-4xl">
                      Meet {profile.display_name}
                    </h1>
                    <p className="mt-1 text-sm text-white/80 sm:text-base">
                      Explore this AI expert&apos;s skills, experience, and portfolio to see how they can help your
                      business.
                    </p>
                  </div>

                  {isClient && !isOwner && isSignedIn && (
                    <div className="mt-2 flex w-full items-center justify-start sm:mt-0 sm:w-auto sm:justify-end">
                      <Button
                        onClick={handleStartChat}
                        disabled={isStartingChat || !profileCompletion.isComplete}
                        className={cn(
                          'flex items-center justify-center gap-2 rounded-full px-5 py-2 text-sm font-semibold shadow-lg',
                          'bg-white text-[#3654e9] hover:bg-slate-50 hover:text-[#2433b8]',
                          'disabled:opacity-60'
                        )}
                        title={
                          !profileCompletion.isComplete
                            ? 'Complete your profile to start a conversation'
                            : 'Start chat'
                        }
                      >
                        {isStartingChat ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Starting...
                          </>
                        ) : (
                          <>
                            <MessageCircle className="h-4 w-4" />
                            Start Chat
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </FadeIn>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">

          {/* Sign up prompt for non-authenticated users */}
          {!isSignedIn && (
            <div
              className="rounded-[40px] p-8 mb-8 text-center"
              style={{
                background: 'linear-gradient(100.97deg, rgba(70, 105, 237, 0.1) 0%, rgba(54, 84, 233, 0.1) 100%)',
                border: '2px solid rgba(70, 105, 237, 0.2)',
              }}
            >
              <div className="w-16 h-16 rounded-full bg-[#4669ed] mx-auto flex items-center justify-center mb-4">
                <UserPlus className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Interested in Hiring this Genius?</h2>
              <p className="text-slate-500 mb-4">Sign up to connect with AI experts and start your project</p>
              <SignUpButton mode="modal">
                <Button
                  className="px-8 py-3 rounded-full text-white font-bold"
                  style={{
                    background: 'linear-gradient(106.66deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                  }}
                >
                  Sign Up Here
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>
              </SignUpButton>
            </div>
          )}

          <div className="flex flex-col lg:flex-row gap-6">

            {/* Left Sidebar */}
            <div className="lg:w-[350px] flex-shrink-0 space-y-6">

              {/* Profile Card */}
              <div className="bg-white rounded-[40px] border border-slate-200 p-8">
                <div className="flex flex-col items-center gap-6">
                  {/* Avatar */}
                  <div className="relative">
                    <div
                      className="w-[120px] h-[120px] rounded-[48px] flex items-center justify-center overflow-hidden"
                      style={{
                        background: profile.profile_picture_url
                          ? 'transparent'
                          : 'linear-gradient(135deg, rgba(70, 105, 237, 1) 0%, rgba(54, 84, 233, 1) 100%)',
                        boxShadow: '0px 4px 12px 0px rgba(70, 105, 237, 0.2)',
                      }}
                    >
                      {profile.profile_picture_url ? (
                        <img
                          src={profile.profile_picture_url}
                          alt={profile.display_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <span className="text-white font-black text-4xl">{initials}</span>
                      )}
                    </div>
                    {/* Online indicator */}
                    {/* <div
                      className="absolute -bottom-1 -right-1 w-[24px] h-[24px] rounded-full bg-emerald-500 border-[4px] border-white"
                      style={{ boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.2)' }}
                    /> */}
                  </div>

                  {/* Name and Headline */}
                  <div className="text-center space-y-2 w-full overflow-hidden">
                    <h1 
                      className="text-[28px] font-bold text-slate-900 w-full max-w-full" 
                      style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                    >
                      {profile.display_name}
                    </h1>
                    {profile.headline && (
                      <p className="text-lg text-slate-500">{profile.headline}</p>
                    )}

                    {/* Status Badges */}
                    <div className="flex flex-wrap justify-center gap-2 pt-2">
                      {(isOwner || isAdmin) && (
                        <>
                          {profile.is_searchable ? (
                            <Badge className="rounded-full px-3 py-1.5 text-xs font-semibold bg-emerald-100 text-emerald-800">
                              <Eye className="h-3 w-3 mr-1" />
                              Public
                            </Badge>
                          ) : (
                            <Badge className="rounded-full px-3 py-1.5 text-xs font-semibold bg-orange-100 text-orange-800">
                              <EyeOff className="h-3 w-3 mr-1" />
                              Private
                            </Badge>
                          )}
                          <Badge className={cn(
                            "rounded-full px-3 py-1.5 text-xs font-semibold",
                            isEnabled ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'
                          )}>
                            {isEnabled ? 'Approved' : 'Pending Approval'}
                          </Badge>
                          {isAdmin && !isOwner && (
                            <Badge className="rounded-full px-3 py-1.5 text-xs font-semibold bg-blue-100 text-blue-800">
                              Admin View
                            </Badge>
                          )}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Edit Profile Button (Owner only) */}
                  {isOwner && (
                    <Link to="/genius/profile" className="w-full">
                      <Button
                        className="w-full bg-[#4669ed] hover:bg-[#2b42a3] text-white rounded-[48px] py-3 px-6 flex items-center justify-center gap-2"
                      >
                        <Edit className="h-5 w-5" />
                        Edit Profile
                      </Button>
                    </Link>
                  )}

                  {/* View Count (Owner or Admin only) */}
                  {(isOwner || isAdmin) && profileWithNewFields.view_count !== undefined && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <Eye className="h-4 w-4" />
                      <span>
                        {profileWithNewFields.view_count === 1
                          ? '1 view'
                          : `${profileWithNewFields.view_count || 0} views`}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Admin Controls - Only visible to admins viewing other profiles */}
              {isAdmin && !isOwner && (
                <div className="bg-slate-100 rounded-[20px] p-6 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      <Star className="h-5 w-5 text-[#4669ed]" />
                      Admin Controls
                    </h3>
                    {isActionPending && (
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <Clock className="h-4 w-4 animate-spin" />
                        <span>Processing...</span>
                      </div>
                    )}
                  </div>

                  {/* Enable/Disable Toggle - Available for all profiles */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-700">Profile Status</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">
                        {isEnabled ? 'Enabled' : 'Disabled'}
                      </span>
                      <Switch
                        checked={isEnabled}
                        onCheckedChange={(checked) => toggleEnabledMutation.mutate({ enable: checked })}
                        disabled={toggleEnabledMutation.isPending}
                      />
                    </div>
                  </div>

                  {/* Featured Toggle - Available for all profiles */}
                  <div className="flex items-center justify-between py-2">
                    <span className="text-sm text-slate-700">Featured Status</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-slate-500">
                        {isFeatured ? 'Featured' : 'Not Featured'}
                      </span>
                      <Switch
                        checked={isFeatured}
                        onCheckedChange={(checked) => toggleFeaturedMutation.mutate({ feature: checked })}
                        disabled={toggleFeaturedMutation.isPending}
                      />
                    </div>
                  </div>

                  {/* Review Actions - Only show if profile is not already approved */}
                  {reviewStatus !== 'approved' && (
                    <div className="flex flex-col gap-2 pt-2">
                      <Button
                        onClick={() => reviewMutation.mutate({ action: 'approve' })}
                        disabled={reviewMutation.isPending}
                        className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full"
                      >
                        <Check className="h-4 w-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => reviewMutation.mutate({ action: 'request_changes' })}
                        disabled={reviewMutation.isPending}
                        className="w-full bg-amber-500 hover:bg-amber-600 rounded-full"
                      >
                        <AlertTriangle className="h-4 w-4 mr-2" />
                        Request Changes
                      </Button>
                      <Button
                        onClick={() => reviewMutation.mutate({ action: 'reject' })}
                        disabled={reviewMutation.isPending}
                        variant="destructive"
                        className="w-full rounded-full"
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Availability Card */}
              <div className="bg-white rounded-[20px] border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Availability</h3>
                </div>
                <div>
                  {availabilityBadge ? (
                    <Badge className={cn("rounded-full px-3 py-1.5 text-xs font-semibold", availabilityBadge.className)}>
                      {availabilityBadge.label}
                    </Badge>
                  ) : (
                    <p className="text-sm text-slate-500">Not specified</p>
                  )}
                </div>
              </div>

              {/* Rate Range Card */}
              <div className="bg-white rounded-[20px] border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <DollarSign className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Rate Range</h3>
                </div>
                <div>
                  {rateDisplay ? (
                    <p className="text-slate-900 font-medium">{rateDisplay}</p>
                  ) : (
                    <p className="text-sm text-slate-500">Not specified</p>
                  )}
                </div>
              </div>

              {/* Details Card */}
              <div className="bg-white rounded-[20px] border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MapPin className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Details</h3>
                </div>
                <div className="space-y-3">
                  {profile.linkedin_url ? (
                    <a
                      href={profile.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#4669ed] hover:underline"
                    >
                      <Linkedin className="h-4 w-4" />
                      LinkedIn Profile
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                  {profile.website_url ? (
                    <a
                      href={profile.website_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-sm text-[#4669ed] hover:underline"
                    >
                      <Globe className="h-4 w-4" />
                      Personal Website
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  ) : null}
                  {!profile.linkedin_url && !profile.website_url && (
                    <p className="text-sm text-slate-500">No links available</p>
                  )}
                </div>
              </div>

              {/* Languages Card */}
              <div className="bg-white rounded-[20px] border border-slate-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Globe className="h-5 w-5 text-slate-700" />
                  <h3 className="text-lg font-semibold text-slate-900">Languages</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {languages.length > 0 ? (
                    languages.map((language) => (
                      <Badge
                        key={language}
                        className="bg-[#d1e0fc] text-[#2b42a3] rounded-full px-3 py-1.5 text-xs font-semibold"
                      >
                        {language}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-slate-500">No languages specified</p>
                  )}
                </div>
              </div>

              {/* Video Introductions Card - Inline playable with PiP support */}
              {(videoIntroUrls.length > 0 || isOwner || isAdmin) && (
                <div className="bg-white rounded-[20px] border border-slate-200 p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Video className="h-5 w-5 text-slate-700" />
                    <h3 className="text-lg font-semibold text-slate-900">Video Introduction</h3>
                  </div>
                  <div className="flex flex-col gap-4">
                    {videoIntroUrls.length > 0 ? (
                      videoIntroUrls.map((url, index) => {
                        const videoInfo = getVideoEmbedInfo(url);
                        return (
                          <div key={index} className="space-y-2">
                            {videoInfo.embedUrl ? (
                              <div className="relative aspect-video bg-black rounded-2xl overflow-hidden">
                                <iframe
                                  src={videoInfo.embedUrl}
                                  className="w-full h-full"
                                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                                  allowFullScreen
                                  title={`Video Introduction ${videoIntroUrls.length > 1 ? index + 1 : ''}`}
                                />
                              </div>
                            ) : (
                              <a
                                href={url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-4 py-3 rounded-xl border border-slate-200 text-sm text-[#4669ed] hover:bg-slate-50 transition-colors"
                              >
                                <PlayCircle className="h-4 w-4" />
                                Watch Video Introduction {videoIntroUrls.length > 1 ? `${index + 1}` : ''}
                              </a>
                            )}
                            {videoInfo.type && (
                              <div className="flex items-center gap-2 text-xs text-slate-500">
                                <Badge variant="outline" className="text-xs capitalize">
                                  {videoInfo.type}
                                </Badge>
                                <span>Use fullscreen or picture-in-picture for expanded view</span>
                              </div>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-slate-500">No video introductions available</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Right Content */}
            <div className="flex-1 space-y-6">

              {/* What I Do Section */}
              <div className="bg-white rounded-[40px] border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Briefcase className="h-6 w-6 text-slate-700" />
                  <h3 className="text-2xl text-slate-900">What I Do</h3>
                </div>
                {profile.bio ? (
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {profile.bio}
                  </p>
                ) : (
                  <p className="text-slate-400 text-center">No bio available</p>
                )}
              </div>

              {/* Skills & Expertise Section */}
              <div className="bg-white rounded-[40px] border border-slate-200 p-8">
                <div className="flex items-center gap-3 mb-6">
                  <Code2 className="h-6 w-6 text-slate-700" />
                  <h3 className="text-2xl text-slate-900">Skills & Expertise</h3>
                </div>
                <div className="flex flex-wrap gap-2">
                  {skills.length > 0 ? (
                    skills.map((skill) => (
                      <span
                        key={skill}
                        className="px-4 py-2 rounded-[20px] bg-[#d1e0fc] text-[#2b42a3] font-semibold text-sm"
                      >
                        {skill}
                      </span>
                    ))
                  ) : (
                    <p className="text-slate-400 text-center">No skills specified</p>
                  )}
                </div>
              </div>

              {/* Work Experience Section */}
              <WorkExperienceSection
                freelancerProfileId={id}
                isOwner={isOwner}
              />

              {/* Portfolio Projects Section */}
              <PortfolioSection
                freelancerProfileId={id}
                isOwner={isOwner}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      <StartChatConfirmationDialog
        open={showConfirmDialog}
        onOpenChange={setShowConfirmDialog}
        freelancerName={profile?.display_name}
        onConfirm={confirmStartChat}
        isStartingChat={isStartingChat}
      />
    </>
  );
}
